/**
 * Item Suggestion Cards
 * Horizontally scrollable cards with food image, name, price, dietary badges.
 * + / − buttons sync directly to cart.
 *
 * Pagination:
 *   - AI returns up to 10 items. We show PAGE_SIZE at a time.
 *   - "More" card reveals the next batch from the already-returned items (no API call).
 *   - Once local items are exhausted and has_more=true, "More" calls onShowMore() to ask AI.
 */

import {
  Add as AddIcon,
  ChevronRight as MoreIcon,
  Remove as RemoveIcon,
  Restaurant as PlaceholderIcon,
} from '@mui/icons-material';
import { Box, Card, Chip, IconButton, Typography } from '@mui/material';
import { useRef, useState } from 'react';
import { useCartStore } from '@/store/useCartStore';

const PAGE_SIZE = 5;

export interface SuggestedItem {
  id: string;
  slug?: string;
  name: string;
  price: number;
  description?: string;
  image_url?: string;
  is_vegetarian?: boolean;
  is_vegan?: boolean;
  is_halal?: boolean;
  is_spicy?: boolean;
  has_required_modifiers?: boolean;
}

interface ItemSuggestionCardsProps {
  items: SuggestedItem[];
  hasMore?: boolean;
  language: 'en' | 'ar';
  onShowMore?: () => void;
  /** Called when an item with required modifiers is tapped — triggers chat modifier flow */
  onAddWithModifiers?: (itemName: string) => void;
}

// ─── Dietary badges ───────────────────────────────────────────────────────────

function DietaryBadges({ item }: { item: SuggestedItem }) {
  const badges = [
    item.is_halal && { label: 'H', title: 'Halal', bg: '#E8F5E9', color: '#2E7D32' },
    item.is_vegan && { label: 'VE', title: 'Vegan', bg: '#F1F8E9', color: '#33691E' },
    item.is_vegetarian && !item.is_vegan && { label: 'V', title: 'Vegetarian', bg: '#F1F8E9', color: '#388E3C' },
    item.is_spicy && { label: '🌶', title: 'Spicy', bg: '#FFF3E0', color: '#E65100' },
  ].filter(Boolean) as { label: string; title: string; bg: string; color: string }[];

  if (!badges.length) return null;

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.25 }}>
      {badges.map((b) => (
        <Chip
          key={b.label}
          label={b.label}
          size="small"
          title={b.title}
          sx={{ height: 14, fontSize: '0.58rem', bgcolor: b.bg, color: b.color, px: 0.25 }}
        />
      ))}
    </Box>
  );
}

// ─── Single card ──────────────────────────────────────────────────────────────

function ItemCard({
  item,
  language,
  onAddWithModifiers,
}: {
  item: SuggestedItem;
  language: 'en' | 'ar';
  onAddWithModifiers?: (itemName: string) => void;
}) {
  const addItem = useCartStore((s) => s.addItem);
  const updateQuantity = useCartStore((s) => s.updateQuantity);
  const cartItems = useCartStore((s) => s.items);
  const [imgError, setImgError] = useState(false);

  const cartQty = cartItems
    .filter((i) => i.menu_item_id === item.id)
    .reduce((sum, i) => sum + i.quantity, 0);

  const cartItemId = cartItems.find((i) => i.menu_item_id === item.id)?.id;

  const handleAdd = () => {
    if (item.has_required_modifiers) {
      // Let the chat AI ask modifier questions inline
      onAddWithModifiers?.(item.name);
      return;
    }
    addItem({
      menu_item_id: item.id,
      name: item.name,
      unit_price: item.price,
      quantity: 1,
      modifiers: [],
    });
  };

  const handleDecrement = () => {
    if (cartItemId) updateQuantity(cartItemId, cartQty - 1);
  };

  const showImage = item.image_url && !imgError;

  return (
    <Card
      elevation={0}
      sx={{
        width: 148,
        flexShrink: 0,
        borderRadius: 2,
        border: '1.5px solid',
        borderColor: cartQty > 0 ? 'primary.main' : 'divider',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        bgcolor: cartQty > 0 ? 'primary.50' : 'background.paper',
        transition: 'border-color 0.15s, background-color 0.15s',
      }}
    >
      {/* Image / placeholder */}
      <Box
        sx={{
          height: 90,
          bgcolor: 'grey.100',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          flexShrink: 0,
        }}
      >
        {showImage ? (
          <Box
            component="img"
            src={item.image_url}
            alt={item.name}
            onError={() => setImgError(true)}
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <PlaceholderIcon sx={{ fontSize: 36, color: 'grey.400' }} />
        )}
      </Box>

      {/* Content */}
      <Box
        sx={{
          p: 0.75,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.25,
          flex: 1,
          direction: language === 'ar' ? 'rtl' : 'ltr',
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            fontSize: '0.73rem',
            lineHeight: 1.25,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textAlign: language === 'ar' ? 'right' : 'left',
          }}
        >
          {item.name}
        </Typography>

        {item.description && (
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              fontSize: '0.63rem',
              lineHeight: 1.25,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textAlign: language === 'ar' ? 'right' : 'left',
            }}
          >
            {item.description}
          </Typography>
        )}

        <DietaryBadges item={item} />

        {/* Price + qty control */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mt: 'auto',
            pt: 0.5,
            direction: 'ltr',
          }}
        >
          <Typography
            variant="caption"
            sx={{ fontWeight: 700, color: 'primary.main', fontSize: '0.78rem' }}
          >
            £{item.price.toFixed(2)}
          </Typography>

          {cartQty === 0 ? (
            <IconButton
              size="small"
              onClick={handleAdd}
              sx={{
                width: 26,
                height: 26,
                bgcolor: 'primary.main',
                color: 'white',
                '&:hover': { bgcolor: 'primary.dark' },
              }}
            >
              <AddIcon sx={{ fontSize: 15 }} />
            </IconButton>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
              <IconButton
                size="small"
                onClick={handleDecrement}
                sx={{
                  width: 22,
                  height: 22,
                  border: '1px solid',
                  borderColor: 'primary.main',
                  color: 'primary.main',
                }}
              >
                <RemoveIcon sx={{ fontSize: 11 }} />
              </IconButton>
              <Typography
                variant="caption"
                sx={{ fontWeight: 700, minWidth: 14, textAlign: 'center', fontSize: '0.73rem' }}
              >
                {cartQty}
              </Typography>
              <IconButton
                size="small"
                onClick={handleAdd}
                sx={{
                  width: 22,
                  height: 22,
                  bgcolor: 'primary.main',
                  color: 'white',
                  '&:hover': { bgcolor: 'primary.dark' },
                }}
              >
                <AddIcon sx={{ fontSize: 11 }} />
              </IconButton>
            </Box>
          )}
        </Box>
      </Box>
    </Card>
  );
}

// ─── Card strip ───────────────────────────────────────────────────────────────

export default function ItemSuggestionCards({
  items,
  hasMore,
  language,
  onShowMore,
  onAddWithModifiers,
}: ItemSuggestionCardsProps) {
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const scrollRef = useRef<HTMLDivElement>(null);

  if (!items || items.length === 0) return null;

  const visibleItems = items.slice(0, visibleCount);
  const hiddenLocalCount = items.length - visibleCount;
  // Show "More" if there are hidden local items OR if AI signalled more exist
  const showMoreCard = hiddenLocalCount > 0 || hasMore;

  const handleMore = () => {
    if (hiddenLocalCount > 0) {
      // Reveal next batch from already-loaded items (no API call)
      setVisibleCount((v) => Math.min(v + PAGE_SIZE, items.length));
      // Scroll to the end after revealing
      setTimeout(() => {
        scrollRef.current?.scrollTo({ left: scrollRef.current.scrollWidth, behavior: 'smooth' });
      }, 50);
    } else if (hasMore) {
      // All local items shown — ask AI for the next batch
      onShowMore?.();
    }
  };

  // Label: show count of remaining local items, or plain "More" for AI load
  const moreLabel =
    hiddenLocalCount > 0
      ? language === 'ar'
        ? `${hiddenLocalCount} المزيد`
        : `${hiddenLocalCount} more`
      : language === 'ar'
        ? 'المزيد'
        : 'More';

  return (
    <Box sx={{ mt: 0.75, position: 'relative' }}>
      <Box
        ref={scrollRef}
        sx={{
          display: 'flex',
          gap: 1,
          overflowX: 'auto',
          // Desktop: thin visible scrollbar. Mobile: hidden (touch scroll).
          pb: { xs: 0.75, sm: 1.25 },
          scrollbarWidth: 'thin' as const,
          '&::-webkit-scrollbar': { height: 4 },
          '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
          '&::-webkit-scrollbar-thumb': { bgcolor: 'divider', borderRadius: 2 },
          '@media (max-width: 599px)': {
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          },
        }}
      >
        {visibleItems.map((item) => (
          <ItemCard
            key={item.id}
            item={item}
            language={language}
            onAddWithModifiers={onAddWithModifiers}
          />
        ))}

        {/* "More" card — local pagination first, then AI */}
        {showMoreCard && (
          <Card
            elevation={0}
            onClick={handleMore}
            sx={{
              width: 80,
              flexShrink: 0,
              borderRadius: 2,
              border: '1.5px dashed',
              borderColor: 'primary.light',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              gap: 0.5,
              px: 0.5,
              bgcolor: 'transparent',
              transition: 'border-color 0.15s, background-color 0.15s',
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: 'action.hover',
              },
            }}
          >
            <MoreIcon sx={{ color: 'primary.main', fontSize: 22 }} />
            <Typography
              variant="caption"
              sx={{
                color: 'primary.main',
                fontSize: '0.63rem',
                textAlign: 'center',
                fontWeight: 600,
                lineHeight: 1.2,
              }}
            >
              {moreLabel}
            </Typography>
          </Card>
        )}
      </Box>
    </Box>
  );
}
